import { useState } from 'react';
import { useBrand } from '@/shared/brand/useBrand';
import { useT } from '@/shared/i18n/useT';
import { savePendingBooking, type PendingBooking } from './pendingBooking';
import { signInWithGoogle } from './signInWithGoogle';

export interface GoogleSignInButtonProps {
  /** Overrides the brand's default landing path. */
  readonly next?: string;
  /**
   * Booking in progress, parked before the redirect. Pass it from the booking
   * flow — omitting it means the client comes back to an empty selection.
   */
  readonly pendingBooking?: Omit<PendingBooking, 'savedAt'>;
  /** Shared phones: make Google ask which account. */
  readonly promptSelectAccount?: boolean;
  /** Surface the failure in the caller's own error area. */
  readonly onError?: (error: unknown) => void;
}

/**
 * "Continue with Google" — the primary sign-in on both brands, with OTP kept
 * underneath as the path that always works (docs/ARCHITECTURE.md §3).
 */
export function GoogleSignInButton({
  next,
  pendingBooking,
  promptSelectAccount = false,
  onError,
}: GoogleSignInButtonProps) {
  const t = useT();
  const brand = useBrand();
  const [busy, setBusy] = useState(false);

  async function handleClick() {
    setBusy(true);
    // Park first: once signInWithGoogle resolves the tab is already leaving.
    if (pendingBooking) savePendingBooking(pendingBooking);
    try {
      await signInWithGoogle({
        brand: brand.id,
        ...(next === undefined ? {} : { next }),
        promptSelectAccount,
      });
    } catch (error) {
      setBusy(false);
      onError?.(error);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className="nt-control flex w-full gap-3"
    >
      <GoogleMark />
      <span>{busy ? t('auth.google.pending') : t('auth.google.continue')}</span>
    </button>
  );
}

/**
 * Google's mark, at Google's exact colors.
 *
 * The only sanctioned exception to the "no hardcoded hex" rule in CLAUDE.md:
 * these are another company's brand colors under their sign-in branding
 * guidelines, not ours to theme. They stay identical in light and dark mode,
 * which is why the button keeps a `bg-surface` plate behind it.
 */
function GoogleMark() {
  return (
    <svg
      viewBox="0 0 18 18"
      className="h-[18px] w-[18px] shrink-0"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
